import { useEffect } from 'react'
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './lib/auth'
import { useAccountClosure } from './lib/account-closure'
import AlbumList from './components/album-list'
import MemoirsHome from './components/memoirs-home'
import MemoirWorkspace from './components/memoir-workspace'
import AccessLogPage from './pages/access-log'
import BillingLink from './components/billing-link'
import AccountClosureBanner, { AccountLocked } from './components/account-closure-banner'
import AccountPage from './pages/account'
import LoginForm from './components/login-form'
import PaymentSignup from './components/payment-signup'
import PaymentSuccess from './pages/payment-success'
import PaymentCancelled from './pages/payment-cancelled'
import PublicAlbumView from './components/public-album-view'
import LinkUnavailable from './components/link-unavailable'
import ExportStatus from './components/export-status'
import Terms from './pages/terms'
import Privacy from './pages/privacy'
import { LegalFooter } from './components/legal/legal-page'
import { TERMS_PATH, PRIVACY_PATH } from './lib/legal'
import memoarLogo from './assets/memoar-logo.png'

// The deletion banner (SOL-201) is rendered here, once, above whichever branch
// AppRoutes picks: terms, privacy, view links and the Stripe return pages all
// get it without each branch remembering to. It renders nothing for anyone who
// is signed out.
function App(): JSX.Element {
  return (
    <>
      <AccountClosureBanner />
      <AppRoutes />
    </>
  )
}

function AppRoutes(): JSX.Element {
  const { user, isAuthenticated, isLoading, logout, retry } = useAuth()
  const { closure } = useAccountClosure()
  const navigate = useNavigate()
  const location = useLocation()

  // Correct the URL after login. Rendering is auth-STATE-driven: when the auth
  // store flips to authenticated, App immediately re-renders into the routeless
  // authenticated branch below, which unmounts LoginForm mid-flight — so
  // LoginForm's own `onSuccess={() => navigate('/')}` races that teardown and
  // the address bar can get stuck on `/login`. Redirecting here, where the
  // state actually flips, is reliable because this effect is owned by App, not
  // by the component being torn down.
  useEffect(() => {
    if (isAuthenticated && location.pathname === '/login') {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, location.pathname, navigate])

  // The Terms and Privacy Policy (SOL-197) are hoisted for the same reason as
  // the view-link page below: Stripe's checkout and the landing link to them,
  // and they must render the same signed in, signed out, or mid-bootstrap.
  if (location.pathname === TERMS_PATH || location.pathname === PRIVACY_PATH) {
    return (
      <Routes>
        <Route path={TERMS_PATH} element={<Terms />} />
        <Route path={PRIVACY_PATH} element={<Privacy />} />
      </Routes>
    )
  }

  // The public view-link page (SOL-149) is deliberately hoisted above every
  // auth branch below: recipients have no account, and the page must render
  // identically whether or not a session exists or is still bootstrapping.
  // The Stripe return routes solve the same problem by being duplicated into
  // all three <Routes> blocks; one hoisted route beats a third copy.
  if (location.pathname.startsWith('/view/')) {
    return (
      <Routes>
        <Route path="/view/:token" element={<PublicAlbumView />} />
        {/* Malformed /view/ paths (truncated paste, extra segments) get the
            same opaque message as a revoked/expired link — no distinguishing
            "this URL is broken" from "this link is gone" for a recipient. */}
        <Route
          path="*"
          element={<LinkUnavailable />}
        />
      </Routes>
    )
  }

  if (isLoading) {
    // The Stripe return routes are deliberately rendered even while the auth
    // bootstrap is in flight: a customer coming back from checkout lands here
    // with a session that has not been exchanged yet, and gating those pages
    // on auth makes them flicker or stall for no reason. They don't read the
    // session at all.
    return (
      <Routes>
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancelled" element={<PaymentCancelled />} />
        <Route
          path="*"
          element={<div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}
        />
      </Routes>
    )
  }

  if (isAuthenticated && user) {
    return (
      <div className="app">
        <header className="app__header">
          <div className="app__logo">
            <img src={memoarLogo} alt="Memoar" className="app__logo-img" />
            <span className="app__logo-text">memoar</span>
          </div>
          <div className="app__user-info">
            <span className="app__welcome">Welcome back</span>
            <span className="app__user-email">{user.email}</span>
          </div>
          <div className="app__header-actions">
            <Link className="billing-link__btn" to="/account">Account</Link>
            <BillingLink />
            <button
              className="app__logout-btn"
              onClick={() => {
                logout()
                navigate('/')
              }}
            >
              Sign Out
            </button>
          </div>
        </header>
        <main className="app__main">
          {/* /album/:albumId/export/:hash is hoisted out to its own route
              rather than being state inside AlbumList (SOL-79 Task 10): it is
              the page a "your download is ready" email links to, so it has to
              be reachable by URL on its own, not only by clicking through the
              album grid. Every other authenticated path still renders
              AlbumList exactly as before. */}
          {closure && closure.phase !== 'closing' ? (
            // The last day, and while the job runs: the server answers 423
            // ACCOUNT_LOCKED to everything but cancelling, so the routes would
            // only show errors.
            <AccountLocked />
          ) : (
          <Routes>
            <Route path="/album/:albumId/export/:hash" element={<ExportStatus />} />
            <Route path="/account" element={<AccountPage />} />
            {/* WorkOS user id (`user_01...`); this is the JWT `sub` the API
                authorizes against. `user.sub` no longer exists. */}
            {/* SOL-204: home is the user's Memoirs; the album workspace is
                always scoped, to one of them or to what was shared in. */}
            <Route path="/" element={<MemoirsHome />} />
            <Route path="/memoir/:memoirId" element={<MemoirWorkspace currentUserId={user.id} />} />
            <Route path="/memoir/:memoirId/access-log" element={<AccessLogPage />} />
            <Route path="/shared" element={<AlbumList currentUserId={user.id} scope={{ kind: 'shared' }} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          )}
        </main>
        <LegalFooter className="app__legal" />
      </div>
    )
  }

  // Not authenticated, but a stored user record survived: the refresh token is
  // still in storage and the bootstrap failed on a network or upstream fault
  // (`clearSession()` wipes the user record, so a genuinely rejected session
  // never reaches here). Falling through to the routes below would show a
  // customer who has already paid the "buy Memoar" page, with reload as the
  // only way out. Offer the retry instead.
  //
  // The Stripe return routes stay reachable for the same reason they are
  // rendered during `isLoading`: they don't read the session, and a customer
  // coming back from checkout must see their receipt page regardless.
  if (user) {
    return (
      <Routes>
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-cancelled" element={<PaymentCancelled />} />
        {/* Kept reachable so the "Sign in" button on /payment-success still
            works for someone carrying a stale stored session. */}
        <Route path="/login" element={<LoginForm onSuccess={() => navigate('/')} />} />
        <Route
          path="*"
          element={
            <div className="app app--interrupted">
              <div className="app__reconnect">
                <h1 className="app__reconnect-title">We couldn't reach your archive</h1>
                <p className="app__reconnect-text">
                  Your session is still here — the connection isn't. This is usually
                  temporary.
                </p>
                <button className="app__reconnect-btn" onClick={retry}>
                  Try again
                </button>
                <button
                  className="app__reconnect-signout"
                  onClick={() => {
                    logout()
                    navigate('/')
                  }}
                >
                  Sign out instead
                </button>
              </div>
            </div>
          }
        />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<PaymentSignup onCancel={undefined} />} />
      {/* On success the auth store flips to authenticated and this whole
          subtree is replaced by the dashboard; the navigate just makes sure
          the URL doesn't stay on /login. */}
      <Route path="/login" element={<LoginForm onSuccess={() => navigate('/')} />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
      {/* Every other path — concretely /album/:albumId/export/:hash, what a
          "your download is ready" email links to — used to match nothing
          here and render a blank document: a signed-out visitor is exactly
          who clicks that link from a phone or a second device. LoginForm,
          not a redirect to "/", and deliberately no navigate() on success:
          once isAuthenticated flips, App re-renders into the authenticated
          branch's own <Routes> against this SAME location.pathname, so the
          export page renders where the visitor already is — no
          return-to-URL machinery needed. */}
      <Route path="*" element={<LoginForm onSuccess={() => {}} />} />
    </Routes>
  )
}

export default App
